import * as React from "react";

import { type Device } from "@/shared/domain/devices";
import { useDeleteDevices } from "@/shared/domain/devices/hooks/useDeleteDevices";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import {
  Dialog,
  Field,
  Radio,
  RadioGroup,
  RobotoButton,
  TextInput,
} from "@/shared/foundation/primitives";
import { isDeleteConfirmed } from "@/shared/text";

import {
  collectDeviceFailures,
  DeviceFailureAlert,
} from "../DeviceFailureAlert";

import styles from "./DeleteDeviceDialog.module.css";

type ConfigurationsChoice = "delete" | "keep";

interface DeleteDeviceDialogProps {
  /** The devices to delete; an empty list keeps the dialog closed. */
  devices: readonly Device[];
  onClose: () => void;
  /**
   * Called after each attempt that deleted at least one device, with the
   * devices that failed. The failed ones stay in the dialog for a retry; an
   * empty list means every device is gone.
   */
  onDeleted: (failed: readonly Device[]) => void;
}

/**
 * Confirms deleting one device or several. When `release-asset-store` is on
 * for the devices' org, the user chooses once, for every device, whether the
 * devices' configurations are deleted too or kept in the organization's
 * assets. A single device must be confirmed by typing its ID.
 *
 * An alert dialog, like `DeleteAgentDialog`: the backdrop does not dismiss it,
 * Escape and Cancel do, and neither does while a delete is running, so a late
 * failure list still has somewhere to show.
 */
export function DeleteDeviceDialog({
  devices,
  onClose,
  onDeleted,
}: DeleteDeviceDialogProps) {
  const open = devices.length > 0;
  const deleteDevices = useDeleteDevices();
  return (
    <Dialog
      role="alertdialog"
      open={open}
      onOpenChange={(next) => {
        if (!next && !deleteDevices.isPending) {
          onClose();
        }
      }}
    >
      <Dialog.Content>
        {open && (
          // Mounted per opening, so each opening starts from a clean form.
          <DeleteDeviceForm
            devices={devices}
            deleteDevices={deleteDevices}
            onDeleted={onDeleted}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
}

function DeleteDeviceForm({
  devices,
  deleteDevices,
  onDeleted,
}: Pick<DeleteDeviceDialogProps, "devices" | "onDeleted"> & {
  deleteDevices: ReturnType<typeof useDeleteDevices>;
}) {
  const canKeepConfigurations = useCheckFeatureFlag(
    "release-asset-store",
    devices[0].org_id,
  );
  // Only a delete of one device asks the user to type its ID; a delete of
  // several skips it, as the datasets grid's bulk delete does. Fixed when the
  // dialog opens, so a bulk retry left with one device still skips it.
  const [isBulk] = React.useState(() => devices.length > 1);
  // Shrinks to the devices that failed, so a retry sends only those.
  const [remaining, setRemaining] = React.useState(devices);
  const [configurationsChoice, setConfigurationsChoice] =
    React.useState<ConfigurationsChoice>("delete");
  const [confirmText, setConfirmText] = React.useState("");
  // The last attempt's failure messages keyed by device ID, and how many
  // devices that attempt tried to delete.
  const [failures, setFailures] = React.useState<{
    attempted: number;
    messages: ReadonlyMap<string, string>;
  }>({ attempted: 0, messages: new Map() });

  const isDeleting = deleteDevices.isPending;
  const confirmed =
    isBulk ||
    (remaining.length === 1 &&
      isDeleteConfirmed(confirmText, remaining[0].device_id));

  const onConfirm = async () => {
    if (!confirmed || isDeleting) {
      return;
    }
    const results = await deleteDevices.mutateAsync({
      devices: remaining,
      keepConfigurations:
        canKeepConfigurations && configurationsChoice === "keep",
    });

    const { failed, messages } = collectDeviceFailures(
      remaining,
      results,
      "Failed to delete device. Please try again.",
    );

    if (failed.length < remaining.length) {
      onDeleted(failed);
    }
    // Stay open with the failures so the user can retry them.
    setRemaining(failed);
    setFailures({ attempted: remaining.length, messages });
  };

  // Every device is deleted, and the caller closes the dialog on `onDeleted([])`.
  if (remaining.length === 0) {
    return null;
  }

  const count = remaining.length;
  const subject = count === 1 ? "device" : `${count} devices`;
  const possessive = count === 1 ? "device's" : "devices'";
  let confirmLabel = `Delete ${subject}`;
  if (canKeepConfigurations) {
    confirmLabel =
      configurationsChoice === "keep"
        ? `Delete ${subject}, keep configurations`
        : `Delete ${subject} and configurations`;
  }

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        void onConfirm();
      }}
    >
      <Dialog.Title>Delete {subject}</Dialog.Title>
      <DeviceFailureAlert {...failures} isBulk={isBulk} outcome="deleted" />

      <Dialog.Description>
        {count === 1 ? (
          <>
            Deleting <code>{remaining[0].device_id}</code> permanently removes
            the device and its access tokens.
          </>
        ) : (
          <>
            Deleting these {count} devices permanently removes them and their
            access tokens.
          </>
        )}{" "}
        This can&apos;t be undone.
      </Dialog.Description>

      {canKeepConfigurations && (
        <RadioGroup<ConfigurationsChoice>
          legend={`What happens to the ${possessive} configurations`}
          value={configurationsChoice}
          onValueChange={setConfigurationsChoice}
        >
          <Radio<ConfigurationsChoice>
            value="delete"
            label={`Delete the ${possessive} configurations`}
          />
          <Radio<ConfigurationsChoice>
            value="keep"
            label="Keep the configurations in the organization's assets"
          />
        </RadioGroup>
      )}

      {!isBulk && (
        <Field.Root>
          <Field.Label>
            Type <code>{remaining[0].device_id}</code> to confirm
          </Field.Label>
          <Field.Control
            render={<TextInput autoComplete="off" spellCheck={false} />}
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
          />
        </Field.Root>
      )}

      <div className={styles.actions}>
        <Dialog.Close
          disabled={isDeleting}
          render={
            <RobotoButton variant="secondary" disabled={isDeleting}>
              Cancel
            </RobotoButton>
          }
        />
        <RobotoButton
          type="submit"
          variant="destructive"
          loading={isDeleting}
          disabled={!confirmed || isDeleting}
        >
          {confirmLabel}
        </RobotoButton>
      </div>
    </form>
  );
}
